import { callFunction } from './functionsService';
import { validateCoinProductImage } from '../utils/coinProductImage';

export { validateCoinProductImage };

function getImageExtension(file) {
  const mimeExtension = {
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
  }[file.type];

  if (mimeExtension) {
    return mimeExtension;
  }

  const nameExtension = file.name.match(/\.[a-z0-9]+$/i)?.[0]?.toLowerCase();
  return nameExtension || '.jpg';
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string' || !dataUrl.includes(',')) {
        reject(new Error('Failed to read image file.'));
        return;
      }

      resolve(dataUrl.split(',')[1]);
    };

    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });
}

export async function uploadCoinProductImage(file, productUid) {
  const validationError = validateCoinProductImage(file);
  if (validationError) {
    throw new Error(validationError);
  }

  if (!productUid) {
    throw new Error('Product ID is required to upload an image.');
  }

  const imageBase64 = await readFileAsBase64(file);
  const { downloadUrl } = await callFunction('uploadCoinProductImage', {
    productUid,
    contentType: file.type,
    fileExtension: getImageExtension(file),
    imageBase64,
  });

  return downloadUrl;
}

function base64ToBlob(base64, contentType) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: contentType });
}

function saveBlobAsFile(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function downloadCoinProductImage(imageUrl, productUid) {
  if (!imageUrl) {
    throw new Error('No image is available to download.');
  }

  if (!productUid) {
    throw new Error('Product ID is required to download the image.');
  }

  const { imageBase64, contentType, filename } = await callFunction('downloadCoinProductImage', {
    imageUrl,
    productUid,
  });

  if (!imageBase64) {
    throw new Error('Failed to download image.');
  }

  const blob = base64ToBlob(imageBase64, contentType || 'application/octet-stream');
  saveBlobAsFile(blob, filename || `${productUid}.jpg`);
}
